import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import StoryCard from '../components/common/StoryCard';
import { SearchBar } from '../components/common';
import { API_URL } from '../config/api';
import { 
  isNative, 
  cacheStories,
  getCachedStories,
  getNetworkStatus
} from '../mobile';

const StoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [featuredStory, setFeaturedStory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [expandedStory, setExpandedStory] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const handleSearchResults = (results) => {
    setSearchResults(results);
    setSelectedCategory('all');
  };

  const clearSearch = () => {
    setSearchResults(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check network status first
        const networkStatus = await getNetworkStatus();
        
        if (!networkStatus.connected) {
          // Try to load from cache when offline
          setIsOffline(true);
          const cachedStories = await getCachedStories();
          if (cachedStories) {
            setStories(cachedStories);
            setLoading(false);
            return;
          }
        }

        const [storiesRes, statsRes, featuredRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/v1/stories?limit=20`),
          axios.get(`${API_URL}/v1/stats`),
          axios.get(`${API_URL}/v1/stories/top/featured`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/v1/categories`).catch(() => ({ data: [] }))
        ]);
        setStories(storiesRes.data);
        setStats(statsRes.data);
        setFeaturedStory(featuredRes.data);
        setCategories(categoriesRes.data);
        setIsOffline(false);
        
        // Cache stories for offline use (on mobile)
        if (isNative()) {
          cacheStories(storiesRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        // Try to load from cache on error
        const cachedStories = await getCachedStories();
        if (cachedStories) {
          setStories(cachedStories);
          setIsOffline(true);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter stories by category (use search results if available)
  const displayStories = searchResults || stories;
  const filteredStories = selectedCategory === 'all' 
    ? displayStories 
    : displayStories.filter(s => s.category === selectedCategory);
  
  // Remove featured story from list to avoid duplication
  const remainingStories = featuredStory && !searchResults
    ? filteredStories.filter(s => s.cluster_id !== featuredStory.cluster_id)
    : filteredStories;

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <Header />
      <main className="main" data-testid="main-content">
        {/* Offline Indicator */}
        {isOffline && (
          <div className="offline-banner" data-testid="offline-banner">
            📴 You're offline. Showing cached stories.
          </div>
        )}
        
        <div className="date-header">
          <h1>Today's Global News Comparison</h1>
          <div className="date">{today}</div>
        </div>
        
        {/* Search Bar */}
        <div className="search-container">
          <SearchBar onResults={handleSearchResults} />
          {searchResults && (
            <div className="search-results-info">
              <span>{searchResults.length} results found</span>
              <button className="clear-search-btn" onClick={clearSearch}>Clear search</button>
            </div>
          )}
        </div>
        
        {/* Category Filter Tabs */}
        {categories.length > 0 && !searchResults && (
          <div className="category-tabs" data-testid="category-tabs">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`category-${cat.id}`}
              >
                <span className="category-emoji">{cat.emoji}</span>
                <span className="category-name">{cat.name}</span>
                <span className="category-count">{cat.count}</span>
              </button>
            ))}
          </div>
        )}
        
        <div className="stories-container" data-testid="stories-container">
          {loading ? (
            <div className="loading"><p>Loading stories...</p></div>
          ) : stories.length > 0 ? (
            <>
              {/* Featured Story Hero */}
              {featuredStory && selectedCategory === 'all' && (
                <div className="featured-story" data-testid="featured-story">
                  <div className="featured-label">
                    <span className="trending-icon">🔥</span> Top Story
                    {featuredStory.is_breaking && <span className="breaking-badge">BREAKING</span>}
                  </div>
                  <h2 className="featured-title">{featuredStory.title}</h2>
                  <div className="featured-meta">
                    <span className="meta-item">
                      <strong>{featuredStory.source_count}</strong> sources
                    </span>
                    <span className="meta-item">
                      <strong>{featuredStory.regions?.length || 0}</strong> regions
                    </span>
                    {featuredStory.figures?.length > 0 && (
                      <span className="meta-item figures">
                        👤 {featuredStory.figures.slice(0, 2).join(', ')}
                      </span>
                    )}
                    <span className="meta-item category-badge">
                      {featuredStory.category?.toUpperCase()}
                    </span>
                  </div>
                  <div className="featured-regions">
                    {featuredStory.regions?.map(region => (
                      <span key={region} className="region-tag">{region.toUpperCase()}</span>
                    ))}
                  </div>
                  <button 
                    className="featured-cta"
                    onClick={() => setExpandedStory(featuredStory.cluster_id)}
                    data-testid="view-featured-story"
                  >
                    View Full Comparison →
                  </button>
                </div>
              )}
              
              {/* Stories Grid */}
              <div className="stories-grid">
                {remainingStories.map(story => (
                  <StoryCard 
                    key={story.cluster_id} 
                    story={story}
                    expanded={expandedStory === story.cluster_id}
                    onToggle={() => setExpandedStory(
                      expandedStory === story.cluster_id ? null : story.cluster_id
                    )}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h2>No stories available yet</h2>
              <p>News is being ingested and analyzed. Check back soon!</p>
              <p className="hint">The pipeline will automatically fetch news from global sources.</p>
            </div>
          )}
        </div>
      </main>
      <Footer stats={stats} />
    </>
  );
};

export default StoriesPage;
